<template>
  <div class="btn-speech-wrap">
    <button
      ref="refButtonSettings"
      class="button-white button-icon button-icon-bigger button-rounded"
      @click="toggleSettingButton"
      :class="{ active: isVisibleSettings }"
    >
      <IconEllipsis class="icon-medium" />
    </button>

    <div v-if="domain === 'SELF_SERVICE'" class="mob-guide-speech" :class="{ close: !isVisibleHintBox }">
      <span>{{ t('title_guide') }}</span>
      <button type="button" class="close-x" @click="closeHintBox">
        <IconX />
      </button>
    </div>
  </div>

  <NavMenu v-if="isVisibleSettings" :target-el="refButtonSettings" @close="toggleSettingButton">
    <ChatSetting :options="options" />
  </NavMenu>
</template>

<script setup lang="ts">
import ChatSetting from '@/components/domains/chat/common/ChatSetting.vue';
import IconEllipsis from '@/components/icons/IconEllipsis.vue';
import IconX from '@/components/icons/IconX.vue';
import NavMenu from '@/components/shared/composite/NavMenu.vue';
import { TypeCall, TypeDomain } from '@/types/common';
import { ChatSettingOptions } from '@/types/components';
import { CHAT_SETTING_OPTIONS } from '@/utils/constants/chat';

const props = withDefaults(
  defineProps<{
    typeCall?: TypeCall;
    domain?: TypeDomain;
  }>(),
  {
    typeCall: 'GENERAL',
    domain: 'CHAT',
  }
);

const { t } = useI18n();

const refButtonSettings = useTemplateRef('refButtonSettings');
const isVisibleSettings = ref<boolean>(false); // 설정 드롭다운 보임 여부
const isVisibleHintBox = ref<boolean>(true);

const options = computed<ChatSettingOptions[]>(() => {
  return [...(CHAT_SETTING_OPTIONS[props.typeCall] ?? []), ...(props.domain === 'CHAT' ? ['DARK_MODE'] : [])];
});

const toggleSettingButton = () => {
  isVisibleSettings.value = !isVisibleSettings.value;

  if (isVisibleHintBox.value) {
    closeHintBox();
  }
};

const closeHintBox = () => {
  isVisibleHintBox.value = false;
};
</script>

<style scoped lang="scss">
.btn-speech-wrap {
  position: relative;
}
.mob-guide-speech {
  position: absolute;
  background: $color-gray-700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem 0.5rem 1rem;
  border-radius: 0.5rem;
  top: 2.5rem;
  right: -0.5rem;
  z-index: 10;
  opacity: 0;
  animation: fadeIn 0.5s ease-out 0.4s forwards;
  max-width: 180px;
  width: max-content;
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    right: 1.75rem;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0px 5px 7px 5px;
    border-color: transparent transparent $color-gray-700 transparent;
  }
  &.close {
    animation: fadeOut 0.4s ease-out forwards;
    .close-x {
      visibility: hidden;
    }
  }
  span {
    font-size: 0.75rem;
    color: #fff;
    word-break: keep-all;
    flex: 1;
    white-space: break-spaces;
    line-height: $line-height-psmaller;
  }
  .close-x {
    padding: 0.25rem;
    svg {
      color: $color-white;
    }
  }
}

@keyframes fadeIn {
  0% {
    opacity: 0;
    transform: translateY(8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes fadeOut {
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(8px);
  }
}
</style>
